import React from 'react';
import { Card, Message, Form, Section, Select } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { transformDateStringUtcIntoDate } from '../../../utils/date';
import moment from 'moment';
import { DateTimeInput } from '../../../components/date-time-input/dateTimeInput';

import Style from './releaseCard.module.css';
import PropTypes from 'prop-types';
import i18n from 'i18next';

export default function ReleaseCard({ form, onChange, onSetForm, errors, event, rates, rate, onChangeSalesStart, salesStart }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const currentYear = new Date().getFullYear();

    const optionsRates = React.useMemo(() => {
        let options = [];

        if (!rate) {
            return [];
        }

        let rate_list = rates.filter(rte => rte.id !== rate.id);

        options = rate_list.map(rate => {
            let label = t('ticket.rates.optionalSettings.release.relayOnHideRate');
            if (rate.visibility_end_date !== null && rate.quantity === null) {
                // End date and no quantity
                label = `${t('ticket.rates.optionalSettings.release.relayOnDate', {
                    date: transformDateStringUtcIntoDate(rate.visibility_end_date, event).format('YYYY-MM-DD HH:mm:ss')
                })}`;
            }

            if (rate.visibility_end_date === null && rate.quantity !== null) {
                // End date and no quantity
                label = `${t('ticket.rates.optionalSettings.release.relayOnQuantity', { quantity: rate.quantity })}`;
            }

            if (rate.visibility_end_date !== null && rate.quantity !== null) {
                // End date and no quantity
                label = `${t('ticket.rates.optionalSettings.release.relayOnQuantityOrDate', {
                    quantity: rate.quantity,
                    date: transformDateStringUtcIntoDate(rate.visibility_end_date, event).format('YYYY-MM-DD HH:mm:ss')
                })}`;
            }

            return {
                text: (
                    <div className={Style['rate-picker-options']}>
                        <div>
                            <div className={Style['rate-picker-options-label']}>{rate.resolved_name}</div>
                            <div className={Style['rate-picker-options-sublabel']}>{label}</div>
                        </div>
                    </div>
                ),
                value: rate.id,
                key: rate.id
            };
        });

        return options;
    }, [rate, rates, t, event]);

    return (
        <Card.FormStep
            title={t(`ticket.rates.optionalSettings.release.title`)}
            subtitle={t(`ticket.rates.optionalSettings.release.subtitle`)}
            rightComponent={
                <Message type="tips" title={t('ticket.common.tips')} mini>
                    {t('ticket.rates.optionalSettings.release.tips')}
                </Message>
            }
        >
            <Form>
                <Form.Field className={Style['field-selector']}>
                    <Form.Label>{t(`ticket.rates.modalSettingsEvent.startSales`)}</Form.Label>
                    <Select
                        errors={errors['visibilityStartDate'] ? true : false}
                        onChange={value => {
                            if (value === 'date') {
                                // NOW
                                let now = moment.utc().tz(event.timezone);
                                onSetForm({ visibilityStartDate: now, relays: [] }, true);
                            } else if (value === 'publish_event') {
                                onSetForm({ visibilityStartDate: null, relays: [] }, true);
                            } else {
                                onSetForm({ visibilityStartDate: null }, true);
                            }
                            onChangeSalesStart(value);
                        }}
                        options={[
                            { text: t(`ticket.rates.modalSettingsEvent.startOnPublish`), value: 'publish_event' },
                            { text: t(`ticket.rates.modalSettingsEvent.startOnSpecificDate`), value: 'date' },
                            { text: t('ticket.rates.optionalSettings.release.startSaleRelays'), value: 'relays' }
                        ]}
                        value={salesStart || 'publish_event'}
                    />
                </Form.Field>

                {salesStart === 'date' && (
                    <div className={Style['date-inputs']}>
                        <Section>
                            <DateTimeInput
                                className={Style['date-time-input']}
                                timeLabel={t('ticket.common.time.start')}
                                dateLabel={t('ticket.common.date.start')}
                                onChange={date => {
                                    onChange('visibilityStartDate', date);
                                }}
                                value={form.visibilityStartDate}
                                dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15 }}
                                locale={locale}
                                timezone={event.timezone}
                                error={t(errors['visibilityStartDate'])}
                            />
                        </Section>
                    </div>
                )}
                {salesStart === 'relays' && (
                    <Section className={Style['release-card-section']}>
                        <Form.Field>
                            <Form.Label>{t('ticket.rates.optionalSettings.release.relaySelector')}</Form.Label>
                            <Select
                                placeholder={t('ticket.rates.optionalSettings.release.relaySelectorPlaceholder')}
                                onChange={(synth, data) => onChange('relays', [data.value])}
                                options={optionsRates}
                                value={form.relays.length ? form.relays[0] : null}
                            />
                        </Form.Field>
                    </Section>
                )}

                <Form.Field className={Style['field-selector']}>
                    <Form.Label>{t(`ticket.rates.modalSettingsEvent.endSales`)}</Form.Label>
                    <Select
                        errors={errors['visibilityEndDate'] ? true : false}
                        onChange={value => {
                            if (value === 'publish_event') {
                                onSetForm({ visibilityEndDate: null }, true);
                            } else {
                                // NOW
                                let now = moment.utc().tz(event.timezone);
                                onSetForm({ visibilityEndDate: now }, true);
                            }
                        }}
                        options={[
                            { text: t(`ticket.rates.modalSettingsEvent.endOnPublish`), value: 'publish_event' },
                            { text: t(`ticket.rates.modalSettingsEvent.endOnSpecificDate`), value: 'date' }
                        ]}
                        value={form.visibilityEndDate ? 'date' : 'publish_event'}
                    />
                </Form.Field>

                {form.visibilityEndDate && (
                    <div className={Style['date-inputs']}>
                        <Section>
                            <DateTimeInput
                                className={Style['date-time-input']}
                                timeLabel={t('ticket.common.time.end')}
                                dateLabel={t('ticket.common.date.end')}
                                onChange={date => {
                                    onChange('visibilityEndDate', date);
                                }}
                                value={form.visibilityEndDate}
                                dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15, minDate: form.visibilityStartDate }}
                                locale={locale}
                                timezone={event.timezone}
                                error={t(errors['visibilityEndDate'])}
                            />
                        </Section>
                    </div>
                )}
            </Form>
        </Card.FormStep>
    );
}

ReleaseCard.propTypes = {
    form: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onSetForm: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    rate: PropTypes.object.isRequired,
    salesStart: PropTypes.string,
    onChangeSalesStart: PropTypes.func.isRequired,
    event: PropTypes.object.isRequired,
    rates: PropTypes.array.isRequired
};
